import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import clsx from 'clsx';
import Link from './Link';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import circlecheckdarkgrey from '../assets/icons/circle-check_darkgrey_2.svg';
import circleerror from '../assets/icons/circle-error.svg';

const FORM_STATES = {
  DEFAULT: 'default',
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
  BROKEN: 'broken',
};

const schema = yup.object({
  firstname: yup.string().required('Vorname ist ein Pflichtfeld'),
  lastname: yup.string().required('Nachname ist ein Pflichtfeld'),
  email: yup
    .string()
    .email('Bitte geben Sie eine gültige E-Mail-Adresse ein')
    .required('E-Mail ist ein Pflichtfeld'),
  companyWebsite: yup.string(),
  companySize: yup.string().notOneOf(['hidden'], 'Pflichtfeld'),
  message: yup.string().required('Nachricht ist ein Pflichtfeld'),
});

const labelClassName = 'text-sm text-gray-new-90';

const ErrorMessage = ({ onClose }) => (
  <div className="absolute inset-0 flex items-center justify-center p-5" data-test="error-message">
    <div className="relative z-10 flex max-w-sm flex-col items-center text-center">
      <h3 className="font-title text-[32px] font-medium leading-none tracking-extra-tight sm:text-[28px]">
        Hoppla, da ist etwas schiefgelaufen.
      </h3>
      <p className="mt-3.5 max-w-[236px] leading-tight tracking-extra-tight text-gray-new-70">
        Bitte kontaktieren Sie uns direkt auf{' '}
        <a
          href="mailto:hello@blcks.at"
          className="border-b border-white/40 hover:border-white text-white"
        >
          hello@blcks.at
        </a>
      </p>
    </div>
    <button className="absolute right-4 top-4 z-20" type="button" onClick={onClose}>
      <span className="sr-only">Fehlermeldung schließen</span>
    </button>
    <span className="absolute inset-0 bg-[#0E0E11]/40 backdrop-blur-md" />
  </div>
);

const Field = ({ 
  name, 
  label, 
  tag = 'input', 
  type = 'text', 
  placeholder = '', 
  error, 
  isDisabled = false, 
  className = '',
  textareaClassName = '',
  children,
  ...props 
}) => {
  const Tag = tag;
  return (
    <div className={clsx('h-[74px]', className)}>
      <div className="h-full flex flex-col">
        <label htmlFor={name} className="text-[14px] text-gray-new-60 leading-none mb-2">
          {label}
        </label>
        <div className="flex-1">
          <Tag
            id={name}
            name={name}
            type={type}
            placeholder={placeholder}
            disabled={isDisabled}
            className={clsx(
              'block w-full h-[40px] rounded-lg border border-[rgba(120,124,135,0.5)] bg-[#0d0e12] px-4 py-2 text-sm text-white placeholder-gray-new-60 focus:border-white focus:ring-1 focus:ring-white',
              tag === 'textarea' && 'min-h-[170px] resize-none',
              tag === 'select' && 'appearance-none bg-[url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\' fill=\'none\'%3E%3Cpath d=\'M2 4.5L6 8.5L10 4.5\' stroke=\'%23888\' stroke-width=\'1.5\' stroke-linecap=\'round\' stroke-linejoin=\'round\'/%3E%3C/svg%3E")] bg-[right_1rem_center] bg-no-repeat pr-8',
              textareaClassName
            )}
            {...props}
          >
            {children}
          </Tag>
        </div>
        {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
      </div>
    </div>
  );
};

const ContactForm = () => {
  const [formState, setFormState] = useState(FORM_STATES.DEFAULT);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      companySize: 'hidden',
      reasonForContact: 'hidden',
    },
  });

  const onSubmit = async (data) => {
    setFormState(FORM_STATES.LOADING);
    try {
      // Always use port 3001 for the API in development
      const apiUrl = process.env.NODE_ENV === 'development' 
        ? 'http://localhost:3001/api/contact'
        : '/api/contact';

      console.log('Submitting to:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...data,
          redirectUrl: `${window.location.origin}/contact/thank-you`
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Server error:', errorData);
        setFormState(FORM_STATES.ERROR);
        return;
      }

      // If successful, set the success state
      setFormState(FORM_STATES.SUCCESS);
    } catch (error) {
      console.error('Error submitting form:', error);
      setFormState(FORM_STATES.ERROR);
    }
  };

  if (formState === FORM_STATES.SUCCESS) {
    return (
      <div className="flex flex-col h-full rounded-xl bg-gray-new-15 p-8">
        <div className="flex-1 flex flex-col items-center justify-center text-center">
          <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4">
            <img 
              src={circlecheckdarkgrey} 
              alt="Checkmark" 
              className="w-12 h-12" 
            />
          </div>
          <h2 className="text-2xl font-semibold text-white mb-4">Erfolgreich gesendet!</h2>
          <p className="text-gray-300 mb-6 max-w-md">
            Vielen Dank für Ihre Nachricht. Wir haben Ihre Anfrage erhalten und melden uns schnellstmöglich bei Ihnen.
          </p>
          <p className="text-sm text-[#787c87] mb-6 max-w-md">
            Zusätzlich haben wir Ihnen eine Bestätigungs-E-Mail gesendet. <br />
            Ein Klick auf den Link ist optional, hilft aber sicherzustellen, dass unsere Antwort nicht im Spam-Ordner landet.
          </p>
          {/*<div className="p-4 rounded-lg w-full max-w-md text-left">
            <h3 className="text-[#787c87] font-medium mb-2">Keine E-Mail erhalten?</h3>
            <ul className="text-sm text-[#787c87] space-y-2">
              <li>• Überprüfen Sie Ihren Spam-Ordner</li>
              <li>• Warten Sie einige Minuten und aktualisieren Sie Ihren Posteingang</li>
              <li>• Kontaktieren Sie uns unter <a href="mailto:hello@blcks.at" className="text-blue-400 hover:underline">hello@blcks.at</a>, falls Sie keine E-Mail erhalten</li>
            </ul>
          </div>*/}
        </div>
      </div>
    );
  }

  if (formState === FORM_STATES.ERROR) {
    return (
      <div className="flex flex-col items-center justify-center h-full rounded-xl bg-gray-new-15 p-8 text-center">
        <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4">
          <img 
            src={circleerror} 
            alt="Checkmark" 
            className="w-12 h-12" 
          />
        </div>
        <h2 className="text-2xl font-semibold text-white mb-4">Etwas ist schiefgelaufen</h2>
        <p className="text-gray-300 mb-6">
          Beim Senden Ihrer Nachricht ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.
        </p>
        <button
          onClick={() => setFormState(FORM_STATES.DEFAULT)}
          className="px-6 py-2 text-white rounded-lg hover:bg-white hover:text-black transition-colors"
        >
          Erneut versuchen
        </button>
      </div>
    );
  }

  return (
    <form
      className="flex flex-col h-full rounded-xl bg-gray-new-15 p-8"
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className="flex-1 space-y-3">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field
            name="firstname"
            label="Vorname*"
            placeholder="John"
            error={errors.firstname?.message}
            isDisabled={isSubmitting}
            {...register('firstname')}
          />
          <Field
            name="lastname"
            label="Nachname*"
            placeholder="Doe"
            error={errors.lastname?.message}
            isDisabled={isSubmitting}
            {...register('lastname')}
          />
        </div>

        <Field
          name="email"
          label="Email*"
          type="email"
          placeholder="john@company.com"
          error={errors.email?.message}
          isDisabled={isSubmitting}
          {...register('email')}
        />

        <Field
          name="companyWebsite"
          label="Website*"
          placeholder="https://company.com"
          error={errors.companyWebsite?.message}
          isDisabled={isSubmitting}
          {...register('companyWebsite')}
        />

        <Field
          name="companySize"
          label="Unternehmensgröße*"
          tag="select"
          error={errors.companySize?.message}
          isDisabled={isSubmitting}
          {...register('companySize')}
        >
          <option value="hidden" disabled hidden />
          <option value="1-10">1-10 Mitarbeiter</option>
          <option value="11-50">11-50 Mitarbeiter</option>
          <option value="51-200">51-200 Mitarbeiter</option>
          <option value="201-500">201-500 Mitarbeiter</option>
          <option value="501-1000">501-1,000 Mitarbeiter</option>
          <option value="1001+">1,001+ Mitarbeiter</option>
        </Field>

        <Field
          name="reasonForContact"
          label="Grund der Kontaktaufnahme*"
          tag="select"
          error={errors.reasonForContact?.message}
          isDisabled={isSubmitting}
          {...register('reasonForContact')}
        >
          <option value="hidden" disabled hidden />
          <option value="beratung">Beratung zu KI-Automatisierung</option>
          <option value="zusammenarbeit">Interesse an einer Zusammenarbeit</option>
          <option value="dienstleistungen">Anfrage zu Dienstleistungen / Angeboten</option>
          <option value="projektanfrage">Projektanfrage</option>
          <option value="partnerschaft">Partnerschaft / Kooperation</option>
          <option value="karriere">Bewerbung / Karriere</option>
          <option value="sonstiges">Sonstiges</option>
        </Field>

        <div className={clsx('h-[170px]')}>
          <Field
            name="message"
            label="Nachricht*"
            tag="textarea"
            placeholder="Wie können wir Dir helfen?"
            error={errors.message?.message}
            isDisabled={isSubmitting}
            textareaClassName="min-h-[120px]"
            className="h-full"
            {...register('message')}
          />
        </div>
      </div>

      <div className="mt-6 pt-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 ">
          <p className="text-xs text-gray-400 max-w-[250px]">
            Mit dem Absenden dieses Formulars stimmen Sie unserer{' '}
            <Link href="/privacy" className="text-white no-underline hover:text-white">
              Datenschutzerklärung
            </Link> zu!
          </p>
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full sm:w-[176px] h-[46px] rounded-[9999px] bg-[#ffffff] font-geist text-[14px] text-[#0d0e12] font-normal transition-colors hover:opacity-90 disabled:opacity-50 flex items-center justify-center"
          >
            {isSubmitting ? 'Wird gesendet...' : 'Absenden'}
          </button>
        </div>
      </div>
    </form>
  );
};

export default ContactForm;